import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import { idempotencyKeyFor } from '@/lib/http-core';

/**
 * ID43 카테고리-상품 매핑 + 카테고리별 전시 순서.
 * BE 계약(be-catalog): `/admin/v1/categories/{categoryId}/products`.
 *   - GET    매핑된 상품 목록(sortOrder 오름차순)
 *   - POST   { productIds: [...] } 추가
 *   - DELETE /{productId} 제외
 *   - PUT    /order { productIds: [...] } 최종 순서 적용
 * generated OpenAPI 미반영 → adminFetch 로 직접 호출(카테고리 훅 선례).
 *
 * ⚠ 정확한 경로/필드명은 be-catalog 계약 확정 시 정합 (현재 best-guess).
 */
export interface CategoryProduct {
  productId: string;
  name: string;
  thumbnailUrl: string | null;
  sortOrder: number;
}

// be-catalog 계약: CategoryProductMappingView{categoryId, items:[{productId,productCode,name,thumbnailUrl,status,displayOrder}]}
interface CategoryProductItemWire {
  productId?: string | null;
  productCode?: string | null;
  name?: string | null;
  thumbnailUrl?: string | null;
  status?: string | null;
  displayOrder?: number | null;
}
interface MappingViewWire {
  categoryId?: string | null;
  items?: CategoryProductItemWire[] | null;
}
interface ListEnvelope {
  success: boolean;
  data?: MappingViewWire | null;
}

const base = (categoryId: string) => `/admin/v1/categories/${categoryId}/products`;

const categoryProductsKey = (categoryId: string) =>
  ['admin', 'categories', categoryId, 'products'] as const;

const fromWire = (w: CategoryProductItemWire, idx: number): CategoryProduct => ({
  productId: w.productId ?? '',
  name: w.name ?? w.productCode ?? w.productId ?? '',
  thumbnailUrl: w.thumbnailUrl ?? null,
  sortOrder: w.displayOrder ?? idx,
});

export const useCategoryProductsQuery = (categoryId: string | null) =>
  useQuery<CategoryProduct[]>({
    queryKey: categoryProductsKey(categoryId ?? ''),
    enabled: Boolean(categoryId),
    queryFn: async () => {
      const res = await adminFetch(base(categoryId as string));
      if (res.status === 404) return [];
      if (!res.ok) throw new Error(`category products fetch failed: ${res.status}`);
      const env = (await res.json()) as ListEnvelope;
      return (env.data?.items ?? [])
        .map(fromWire)
        .sort((a, b) => a.sortOrder - b.sortOrder);
    },
  });

const writeJson = async (method: string, path: string, body: unknown) => {
  const headers: HeadersInit = {
    'Idempotency-Key': idempotencyKeyFor(method, path, body ?? null),
  };
  const res = await adminFetch(path, {
    method,
    headers,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  if (!res.ok) throw new Error(`${method} ${path} failed: ${res.status}`);
};

export const useCategoryProductsMutations = (categoryId: string) => {
  const qc = useQueryClient();
  const invalidate = () =>
    qc.invalidateQueries({ queryKey: categoryProductsKey(categoryId) });

  const addProducts = useMutation<void, Error, string[]>({
    mutationFn: (productIds) => writeJson('POST', base(categoryId), { productIds }),
    onSuccess: invalidate,
  });

  const removeProduct = useMutation<void, Error, string>({
    mutationFn: (productId) =>
      writeJson('DELETE', `${base(categoryId)}/${productId}`, undefined),
    onSuccess: invalidate,
  });

  /** 최종 순서 시퀀스 적용 (PUT /order). */
  const saveOrder = useMutation<void, Error, string[]>({
    mutationFn: (productIds) => writeJson('PUT', `${base(categoryId)}/order`, { productIds }),
    onSuccess: invalidate,
  });

  return { addProducts, removeProduct, saveOrder };
};
